import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { CouponService } from '../services/coupon.service';
import { Coupon } from '../model/coupon-dto';
import { CouponDialogComponent } from './coupon-dialog/coupon-dialog.component';

@Component({
  selector: 'app-admin-coupons',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatChipsModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatSnackBarModule
  ],
  templateUrl: './admin-coupons.component.html',
  styleUrl: './admin-coupons.component.scss'
})
export class AdminCouponsComponent implements OnInit {

  displayedColumns: string[] = ['code', 'description', 'discountType', 'amount', 'autoApply', 'validity', 'redemptions', 'status', 'actions'];

  coupons: Coupon[] = [];
  loading: boolean = false;
  error: string | null = null;

  // Paginación (page es 0-based, igual que el backend)
  page: number = 0;
  pageSize: number = 10;
  totalElements: number = 0;

  constructor(
    private couponService: CouponService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private spinner: SpinnerVisibilityService
  ) {}

  ngOnInit(): void {
    this.loadCoupons();
  }

  loadCoupons(): void {
    this.loading = true;
    this.error = null;

    this.couponService.getCoupons(this.page, this.pageSize).subscribe({
      next: (response) => {
        if (response.statusCode === '000' && response.data) {
          this.coupons = response.data.list;
          this.totalElements = response.data.totalElements;
        } else {
          this.error = response.message || 'Error al cargar los cupones';
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Error al cargar los cupones: ' + err.message;
        this.loading = false;
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadCoupons();
  }

  openCouponDialog(coupon?: Coupon): void {
    const dialogRef = this.dialog.open(CouponDialogComponent, {
      width: '560px',
      disableClose: true,
      data: { coupon }
    });
    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.showMessage(coupon ? 'Cupón actualizado' : 'Cupón creado');
        this.loadCoupons();
      }
    });
  }

  toggleStatus(coupon: Coupon, isActive: boolean): void {
    this.spinner.show();
    this.couponService.updateCouponStatus(coupon.id, isActive).subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.statusCode === '000') {
          coupon.isActive = isActive;
          this.showMessage(isActive ? 'Cupón habilitado' : 'Cupón deshabilitado');
        } else {
          this.showMessage(response.message || 'Error actualizando el cupón');
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.showMessage('Error actualizando el cupón');
      }
    });
  }

  discountLabel(coupon: Coupon): string {
    return coupon.discountType === 'percentage' ? '%' : 'Fijo';
  }

  amountLabel(coupon: Coupon): string {
    if (coupon.discountType === 'percentage') {
      return `${coupon.amount}%`;
    }
    return this.formatCurrency(coupon.amount);
  }

  redemptionsLabel(coupon: Coupon): string {
    const used = coupon.redemptionsCount ?? 0;
    const max = coupon.maxRedemptionsTotal != null ? coupon.maxRedemptionsTotal : '∞';
    return `${used} / ${max}`;
  }

  validityLabel(coupon: Coupon): string {
    if (!coupon.validFrom && !coupon.validUntil) {
      return 'Sin límite';
    }
    const from = coupon.validFrom ? this.formatDate(coupon.validFrom) : '—';
    const until = coupon.validUntil ? this.formatDate(coupon.validUntil) : '—';
    return `${from} – ${until}`;
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  }

  private formatDate(dateString: string): string {
    // Las fechas de vigencia vienen como 'yyyy-MM-dd' (sin hora). Se parsean
    // como fecha local (no UTC) para evitar que se corran un día por el
    // huso horario de Bogotá (UTC-5) al usar `new Date('yyyy-MM-dd')`.
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, (month || 1) - 1, day || 1).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', { duration: 3500 });
  }
}
